import { assertEnvPresent } from "@every.org/common/src/helpers/getEnv";
import { getCloudinarySignatureRouteSpec } from "@every.org/common/src/routes/me";

import { addToast, ToastType } from "src/components/ToastManager/Toasts";
import { queryApi } from "src/utility/apiClient";
import { HttpStatus } from "src/utility/httpStatus";

export enum UploadStatus {
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
}

type UploadResult =
  | {
      status: UploadStatus.SUCCESS;
      cloudinaryId: string;
    }
  | {
      status: UploadStatus.ERROR;
    };

/**
 * Upload an image file to cloudinary
 */
async function uploadToCloudinary(params: {
  imgDataUrl: string;
  signatureResult: { signature: string; timestamp: string };
}): Promise<UploadResult> {
  const { imgDataUrl, signatureResult } = params;
  const formObj = new FormData();
  formObj.append(
    "api_key",
    assertEnvPresent(
      process.env.NEXT_PUBLIC_CLOUDINARY_PUBLIC_KEY ||
        process.env.REACT_APP_CLOUDINARY_PUBLIC_KEY,
      "CLOUDINARY_PUBLIC_KEY"
    )
  );
  formObj.append("file", imgDataUrl);
  formObj.append("signature", signatureResult.signature);
  formObj.append("timestamp", signatureResult.timestamp);
  imgDataUrl.includes("data:image/svg+xml;") && formObj.append("format", "svg");
  const cloudinaryResponse = await fetch(
    assertEnvPresent(
      process.env.NEXT_PUBLIC_CLOUDINARY_BASE_URL ||
        process.env.REACT_APP_CLOUDINARY_BASE_URL,
      "CLOUDINARY_BASE_URL"
    ),
    {
      method: "post",
      body: formObj,
    }
  );
  if (cloudinaryResponse.status === HttpStatus.OK) {
    return {
      status: UploadStatus.SUCCESS,
      cloudinaryId: (await cloudinaryResponse.json()).public_id,
    };
  }
  if (cloudinaryResponse.status === 429) {
    addToast(ToastType.RATE_LIMITED);

    return {
      status: UploadStatus.ERROR,
    };
  }

  return {
    status: UploadStatus.ERROR,
  };
}

export const MAX_IMAGE_UPLOAD_SIZE = 10485760; // 10 * 1024 * 1024 for the 10MB upload limit

export async function uploadImageToCloudinary(
  base64url: string,
  params?: { [key: string]: string }
) {
  const signatureResult = await queryApi(getCloudinarySignatureRouteSpec, {
    body: params ? params : {},
    routeTokens: {},
    queryParams: {},
  });

  return uploadToCloudinary({
    imgDataUrl: base64url,
    signatureResult,
  });
}

export type ImageFromFileTarget =
  | React.FormEvent<HTMLInputElement>["currentTarget"]
  | React.DragEvent<HTMLDivElement>["dataTransfer"];

export const imageFromFile = (target: ImageFromFileTarget) => {
  return new Promise<{ dataUrl: string } | null>((resolve, reject) => {
    if (!target || !target.files) {
      resolve(null);
      return;
    }
    const file = target.files[0];
    if (!file) {
      // possible for file input to change such that there is not a file present,
      // this prevents downstream errors
      // https://github.com/everydotorg/every.org/issues/5269
      resolve(null);
      return;
    }
    if (file.size > MAX_IMAGE_UPLOAD_SIZE) {
      addToast(ToastType.IMAGE_TOO_LARGE);
      resolve(null);
      return;
    }
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = (e: ProgressEvent<FileReader>) => {
      if (
        e.target &&
        e.target.readyState === 2 &&
        typeof e.target.result === "string"
      ) {
        resolve({ dataUrl: e.target.result });
      }
    };
  });
};
